import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Box,
  Divider,
  TextField,
  Chip,
  Stack,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
} from "@mui/material";
import { connect } from "react-redux";
import {
  assignSpecialSupervisor,
  getSpecialSupervisors,
  removeSpecialSupervisor,
} from "../../../store/rbm/actions";
import { useRequiredAuth } from "../../common/guard/requiredAuth";
import { useEffect, useMemo, useState, useCallback } from "react";
import { getSupervisors } from "../../../store/structure/actions";

const ViewSpecialSupervisorDialog = (props) => {
  const {
    assignmentType = "employee", // "employee" | "position"
    employee,
    position,
    onClose,
    loading,
    supervisors = [],
    getSupervisors,
    getSpecialSupervisors,
    assignSpecialSupervisor,
    removeSpecialSupervisor,
  } = props;

  const { hasPermissions } = useRequiredAuth();
  const [supervisor, setSupervisor] = useState(null);
  const [assignedSupervisors, setAssignedSupervisors] = useState([]);
  const [isLoading, setIsLoading] = useState(false); // loading assigned list
  const [isSubmitting, setIsSubmitting] = useState(false); // create/delete

  const listQuery = useMemo(() => {
    const q = {};
    if (assignmentType === "employee" && employee?.employeePositionId) {
      q.employeePositionId = employee.employeePositionId;
    }
    if (assignmentType === "position" && position?.id) {
      q.positionId = position.id;
    }
    return q;
  }, [assignmentType, employee?.employeePositionId, position?.id]);

  const refreshAssigned = useCallback(() => {
    setIsLoading(true);
    getSpecialSupervisors(listQuery, (status, data) => {
      setIsLoading(status);
      if (!status && Array.isArray(data)) {
        setAssignedSupervisors(data);
      } else if (!status && data) {
        setAssignedSupervisors(Array.isArray(data) ? data : [data]);
      }
    });
  }, [getSpecialSupervisors, listQuery]);

  useEffect(() => {
    refreshAssigned();
    if (!supervisors?.length) getSupervisors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const assignedPositionIds = useMemo(
    () =>
      new Set(
        (assignedSupervisors || [])
          .map((s) => s?.supervisorPositionId)
          .filter(Boolean)
      ),
    [assignedSupervisors]
  );

  const availableSupervisorOptions = useMemo(
    () => (supervisors || []).filter((opt) => !assignedPositionIds.has(opt.id)),
    [supervisors, assignedPositionIds]
  );

  const disableInputs =
    !hasPermissions(["IS_HR", "IS_STRUCTURE_OVERSIGHT"]) ||
    loading ||
    isLoading ||
    isSubmitting;

  const handleOnClose = () => onClose?.(false);

  // --- CREATE (assign) ---
  const handleAdd = () => {
    if (!supervisor) return;

    // API expects at least one subordinate (employeePositionId | subordinatePositionId)
    // and at least one supervisor (supervisorId | supervisorPositionId).
    const payload = {
      employeePositionId:
        assignmentType === "employee" ? employee?.employeePositionId : undefined,
      subordinatePositionId:
        assignmentType === "position" ? position?.id : undefined,
      supervisorId: undefined, // not available in your option shape
      supervisorPositionId: supervisor.id, // your `supervisors` option.id is positionId
    };

    setIsSubmitting(true);
    assignSpecialSupervisor(payload, (status, resp) => {
      setIsSubmitting(false);

      // On success, the API returns { message, data }
      // safest: re-fetch the authoritative list
      if (!status) {
        setSupervisor(null);
        refreshAssigned();
      } else {
        // optionally show an error toast/snackbar here
        // console.error("Assign failed", resp);
      }
    });
  };

  // --- DELETE (deactivate) ---
  const handleRemove = (row) => {
    if (!row?.id) return;
    const confirm = window.confirm(
      `Deactivate special supervisor mapping for "${row.names || row.employeeName || "Supervisor"}"?`
    );
    if (!confirm) return;

    setIsSubmitting(true);
    removeSpecialSupervisor(row.id, (status, resp) => {
      setIsSubmitting(false);
      if (!status) {
        refreshAssigned();
      } else {
        // optionally show an error toast/snackbar here
        // console.error("Remove failed", resp);
      }
    });
  };

  const noOptionsText =
    availableSupervisorOptions.length === 0
      ? "All eligible supervisors are already assigned"
      : "No supervisors found";

  return (
    <Dialog
      aria-labelledby="special-supervisors-dialog-title"
      open
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle id="special-supervisors-dialog-title" className="text-primary">
        <div className="row align-items-center">
          <div className="col-9">
            <h5 className="mb-0">
              Special Supervisors for{" "}
              <span className="text-dark">
                {employee?.lastName} {employee?.firstName}
              </span>{" "}
              on{" "}
              <span className="text-dark">
                {employee?.positionName || position?.name}
              </span>
            </h5>
            <Typography variant="body2" className="text-muted">
              {assignmentType === "employee"
                ? "Assignment scope: Employee"
                : "Assignment scope: Position"}
            </Typography>
          </div>
          <div className="col-3 d-flex justify-content-end">
            <IconButton
              aria-label="close"
              onClick={handleOnClose}
              sx={{ color: (theme) => theme.palette.grey[600] }}
            >
              <span className="material-icons">close</span>
            </IconButton>
          </div>
        </div>
      </DialogTitle>

      <DialogContent className="mt-0 mx-3" dividers>
        <div className="mb-3">
          <Stack direction="row" spacing={1} className="flex-wrap align-items-center">
            <Chip
              label={`Assigned: ${assignedSupervisors.length}`}
              color="primary"
              variant="outlined"
              size="small"
            />
            <Chip
              label={`Available to add: ${availableSupervisorOptions.length}`}
              color="success"
              variant="outlined"
              size="small"
            />
            {(loading || isLoading || isSubmitting) && <CircularProgress size={18} />}
          </Stack>
        </div>

        <div className="row g-3">
          <div className="col-12 col-lg-6">
            <Typography variant="subtitle2" className="mb-2">
              Add a Special Supervisor
            </Typography>
            <Autocomplete
              readOnly={disableInputs}
              size="small"
              id="supervisor"
              value={supervisor}
              options={availableSupervisorOptions}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              onChange={(_, value) => setSupervisor(value || null)}
              getOptionLabel={(option) => option?.name || ""}
              noOptionsText={noOptionsText}
              renderOption={(props, option) => (
                <>
                  <Box component="li" {...props}>
                    <div className="d-flex flex-column">
                      <span className="fw-semibold">{option.name}</span>
                      <small className="text-muted">{option.unitName}</small>
                    </div>
                  </Box>
                  <Divider />
                </>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Supervisor"
                  placeholder="Search by name or unit"
                />
              )}
              disabled={loading || isLoading || isSubmitting}
              className="mb-2"
            />

            <Tooltip
              title={
                supervisor
                  ? "Add selected supervisor"
                  : "Select a supervisor first"
              }
            >
              <span>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleAdd}
                  disabled={!supervisor || disableInputs}
                >
                  Add
                </Button>
              </span>
            </Tooltip>
          </div>

          <div className="col-12 col-lg-6">
            <Typography variant="subtitle2" className="mb-2">
              Already Assigned
            </Typography>

            <div className="table-responsive border rounded">
              <Table size="small" aria-label="assigned-special-supervisors">
                <TableHead>
                  <TableRow>
                    <TableCell className="fw-bold">Name</TableCell>
                    <TableCell className="fw-bold">Position</TableCell>
                    <TableCell className="fw-bold">Unit</TableCell>
                    <TableCell className="fw-bold" align="right">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assignedSupervisors.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <span className="text-muted">
                          No special supervisors assigned.
                        </span>
                      </TableCell>
                    </TableRow>
                  ) : (
                    assignedSupervisors.map((s) => (
                      <TableRow key={`${s.id}-${s.supervisorPositionId || ""}`}>
                        <TableCell>{s.names || s.employeeName || "-"}</TableCell>
                        <TableCell>{s.positionName || "-"}</TableCell>
                        <TableCell>
                          {s.unitName || s.planningEntityName || "-"}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Deactivate mapping">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleRemove(s)}
                                disabled={disableInputs || isSubmitting}
                              >
                                <span className="material-icons">delete_outline</span>
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </DialogContent>

      <DialogActions className="py-3 mx-0 d-flex justify-content-center">
        <Button variant="outlined" onClick={handleOnClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const mapStateToProps = ({ loading,supervisors }) => ({ loading,supervisors });

export default connect(mapStateToProps, {
  getSpecialSupervisors,
  getSupervisors,
  assignSpecialSupervisor,
  removeSpecialSupervisor,
})(ViewSpecialSupervisorDialog);
